import { AdminApi, CountryEnum, DocumentsApi, Document } from 'api';
import useApiCall from 'hooks/use-api-call';
import React, { FunctionComponent, useMemo, useState } from 'react';
import Translate from 'ui/atoms/translate';
import { Modal, makeModalForm, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons } from 'ui/molecules/modal';
import { StateValues } from 'react-use-form-state';
import Hint from 'ui/atoms/hint';
import Grid, { Col } from 'ui/atoms/grid';
import useCountries from 'hooks/use-countries';
import { FileWithDocType } from 'ui/atoms/file-upload';
import Section from 'src/ui/atoms/section';
import FileUploadDocumentContainer from 'src/ui/atoms/file-upload/file-upload-document-container';

export interface ManualReviewCompanyFormFields {
  companyName: string;
  street: string;
  zip: string;
  city: string;
  country: CountryEnum;
  commercialRegisterStatement: FileWithDocType;
  listOfShareholders: FileWithDocType;
  transparencyRegister: FileWithDocType;
  commercialRegisterStatementId?: string;
  listOfShareholdersId?: string;
  transparencyRegisterId?: string;
}

interface Documents {
  commercialRegisterStatement: Document | null;
  listOfShareholders: Document | null;
  transparencyRegister: Document | null;
}

export interface AddManualReviewModalProps {
  onHide: () => void;
  continueReview: () => void;
  fetchInvestorDetails?: () => void;
  investorId: string;
  values: Omit<
    ManualReviewCompanyFormFields,
    'commercialRegisterStatement' | 'listOfShareholders' | 'transparencyRegister'
  > &
    Documents;
}

const ManualReviewCompanyForm = makeModalForm<ManualReviewCompanyFormFields>();

const ManualReviewCompanyFormModal: FunctionComponent<AddManualReviewModalProps> = (props) => {
  const { onHide, continueReview, investorId, fetchInvestorDetails, values } = props;

  const { makeAuthenticatedApi, withApi } = useApiCall();

  const [personValues, setPersonValues] = useState<ManualReviewCompanyFormFields>({
    ...values,
    commercialRegisterStatement: { file: null, documentType: '' },
    listOfShareholders: { file: null, documentType: '' },
    transparencyRegister: { file: null, documentType: '' },
  });
  const [commercialRegisterStatement, setCommercialRegisterStatement] = useState<Document | null>(
    values.commercialRegisterStatement,
  );
  const [listOfShareholders, setListOfShareholders] = useState<Document | null>(values.listOfShareholders);
  const [transparencyRegister, setTransparencyRegister] = useState<Document | null>(values.transparencyRegister);

  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const documentsApi: DocumentsApi = useMemo(() => makeAuthenticatedApi(DocumentsApi), [makeAuthenticatedApi]);

  const onSubmit = (fieldsValues: StateValues<ManualReviewCompanyFormFields>) => {
    withApi(async () => {
      const patchBody = {
        id: investorId,
        adminLegalPersonUpdateRequest: {
          ...fieldsValues,
        },
      };

      if (fieldsValues.commercialRegisterStatement?.file) {
        const commercialRegisterStatementResponse = await documentsApi.adminDocumentsCreate({
          file: fieldsValues.commercialRegisterStatement?.file,
        });
        patchBody.adminLegalPersonUpdateRequest.commercialRegisterStatementId = commercialRegisterStatementResponse.id;
      } else if (commercialRegisterStatement) {
        patchBody.adminLegalPersonUpdateRequest.commercialRegisterStatementId = values.commercialRegisterStatementId;
      }
      if (fieldsValues.listOfShareholders?.file) {
        const listOfShareholdersResponse = await documentsApi.adminDocumentsCreate({
          file: fieldsValues.listOfShareholders?.file,
        });
        patchBody.adminLegalPersonUpdateRequest.listOfShareholdersId = listOfShareholdersResponse.id;
      } else if (listOfShareholders) {
        patchBody.adminLegalPersonUpdateRequest.listOfShareholdersId = values.listOfShareholdersId;
      }
      if (fieldsValues.transparencyRegister?.file) {
        const transparencyRegisterResponse = await documentsApi.adminDocumentsCreate({
          file: fieldsValues.transparencyRegister?.file,
        });
        patchBody.adminLegalPersonUpdateRequest.transparencyRegisterId = transparencyRegisterResponse.id;
      } else if (transparencyRegister) {
        patchBody.adminLegalPersonUpdateRequest.transparencyRegisterId = values.transparencyRegisterId;
      }

      await adminApi.adminInvestorsLegalPersonCreate(patchBody);

      onHide();
      fetchInvestorDetails!();
      continueReview();
    });
  };

  return (
    <>
      <Modal onClose={onHide}>
        <ManualReviewCompanyForm
          onChange={(values: StateValues<ManualReviewCompanyFormFields>) => {
            setPersonValues(values);
          }}
          onSubmit={(values: StateValues<ManualReviewCompanyFormFields>) => {
            onSubmit(values);
          }}
          i18nKey="dashboardIssuerInvestorDetails.editCompanyDataForm"
          initial={{
            companyName: personValues.companyName,
            street: personValues.street,
            zip: personValues.zip,
            city: personValues.city,
            country: personValues.country,
            commercialRegisterStatement: { file: null, documentType: null },
            listOfShareholders: { file: null, documentType: null },
            transparencyRegister: { file: null, documentType: null },
          }}
        >
          <ModalHeader>
            <Translate name="kycComplianceDetails.addManualReview.title" />
          </ModalHeader>
          <ModalContent>
            <Section spacing="regular">
              <Hint variant="info">
                <Translate name="kycComplianceDetails.addManualReview.hint" />
              </Hint>
            </Section>

            <Section spacing="regular">
              <ManualReviewCompanyForm.Group name="companyName" required={true}>
                <ManualReviewCompanyForm.Input autoFocus={true} />
              </ManualReviewCompanyForm.Group>
              <ManualReviewCompanyForm.Group name="street" required={true}>
                <ManualReviewCompanyForm.Input />
              </ManualReviewCompanyForm.Group>
              <Grid>
                <Col leftCol width={4}>
                  <ManualReviewCompanyForm.Group name="zip" required={true}>
                    <ManualReviewCompanyForm.Input />
                  </ManualReviewCompanyForm.Group>
                </Col>
                <Col rightCol width={8}>
                  <ManualReviewCompanyForm.Group name="city" required={true}>
                    <ManualReviewCompanyForm.Input />
                  </ManualReviewCompanyForm.Group>
                </Col>
              </Grid>
              <ManualReviewCompanyForm.Group name="country" required={true}>
                <ManualReviewCompanyForm.Select
                  options={useCountries()}
                  value={personValues.country}
                  onChange={(country: CountryEnum) => {
                    setPersonValues({ ...personValues, country });
                  }}
                ></ManualReviewCompanyForm.Select>
              </ManualReviewCompanyForm.Group>
            </Section>
            {commercialRegisterStatement && (
              <ManualReviewCompanyForm.Group name="commercialRegisterStatement">
                <FileUploadDocumentContainer
                  uploadedFile={{
                    file: {
                      name: commercialRegisterStatement.name,
                      size: commercialRegisterStatement.sizeInBytes,
                    } as File,
                  }}
                  onRemoveDocument={() => setCommercialRegisterStatement(null)}
                />
              </ManualReviewCompanyForm.Group>
            )}
            {!commercialRegisterStatement && (
              <ManualReviewCompanyForm.Group name="commercialRegisterStatement" required={true}>
                <ManualReviewCompanyForm.FileUpload />
              </ManualReviewCompanyForm.Group>
            )}
            {listOfShareholders && (
              <ManualReviewCompanyForm.Group name="listOfShareholders">
                <FileUploadDocumentContainer
                  uploadedFile={{
                    file: {
                      name: listOfShareholders.name,
                      size: listOfShareholders.sizeInBytes,
                    } as File,
                  }}
                  onRemoveDocument={() => setListOfShareholders(null)}
                />
              </ManualReviewCompanyForm.Group>
            )}
            {!listOfShareholders && (
              <ManualReviewCompanyForm.Group name="listOfShareholders" required={true}>
                <ManualReviewCompanyForm.FileUpload />
              </ManualReviewCompanyForm.Group>
            )}
            {transparencyRegister && (
              <ManualReviewCompanyForm.Group name="transparencyRegister">
                <FileUploadDocumentContainer
                  uploadedFile={{
                    file: {
                      name: transparencyRegister.name,
                      size: transparencyRegister.sizeInBytes,
                    } as File,
                  }}
                  onRemoveDocument={() => setTransparencyRegister(null)}
                />
              </ManualReviewCompanyForm.Group>
            )}
            {!transparencyRegister && (
              <ManualReviewCompanyForm.Group name="transparencyRegister">
                <ManualReviewCompanyForm.FileUpload />
              </ManualReviewCompanyForm.Group>
            )}
          </ModalContent>
          <ModalFooter>
            <ModalFooterButtons
              actionButtons={[
                {
                  name: 'send',
                  content: <Translate name="kycComplianceDetails.addManualReview.reviewPersonsButton" />,
                  variant: 'primary',
                  size: 'large',
                  type: 'submit',
                },
              ]}
            />
          </ModalFooter>
        </ManualReviewCompanyForm>
      </Modal>
    </>
  );
};

export default ManualReviewCompanyFormModal;
